import { useEffect, useState } from "react";
import Meta from "@components/utils/meta";
import Header from "@components/header/header";
import Footer from "@components/footer/footer";
import BackToTop from "@components/back-to-top";
import AppointmentModal from "@components/utils/modal";
import PhoneLink from "@components/utils/phone-link";
import CookieBanner from "@components/consent/CookieBanner";
import BusinessJsonLd from "@components/utils/business-jsonld";
import dynamic from "next/dynamic";
// interaction-driven UI: the overlay chunk (component + search lib) loads on
// FIRST OPEN instead of taxing every page's hydration (LCP render-delay,
// 2026-09-16); it stays mounted afterwards so repeat opens and the close
// animation behave exactly as before.
const SearchOverlay = dynamic(() => import("@components/search/SearchOverlay"), { ssr: false });
import { OPEN_APPOINTMENT_EVENT, OPEN_SEARCH_EVENT, openAppointmentModal } from "@components/strapi/implementation/shared-ui";
import { BaseProps } from "@lib/utils/layout";

interface LayoutProps extends BaseProps {
  children: React.ReactNode;
}

/**
 * The page chrome.
 *
 * The appointment modal lives here rather than on individual pages: any
 * «Κλείστε ραντεβού» control anywhere on the site opens the same dialog, and
 * the callback form is the site's primary conversion (D47). Hoisting it means
 * a section deep in a dynamic zone can trigger it without each page wiring up
 * its own copy. The search overlay is hoisted for the same reason — the
 * header magnifier, the mobile drawer and the keyboard shortcuts all open
 * the one instance.
 *
 * Analytics is NOT mounted here. GA4 loads through @next/third-parties gated on
 * Consent Mode v2 (D22), so it belongs with the consent banner rather than in
 * the layout, where it would fire before the visitor has answered.
 */
export default function Layout(props: LayoutProps) {
  const [showModal, setShowModal] = useState(false);
  // which θέση opened the dialog — rides into appointment_submit as
  // `modal:<source>` so opens and submits stay attributable (P11-d)
  const [modalSource, setModalSource] = useState("unknown");
  const [showSearch, setShowSearch] = useState(false);
  // latches true on the first open — gates the dynamic overlay's mount
  const [searchMounted, setSearchMounted] = useState(false);
  useEffect(() => {
    if (showSearch) setSearchMounted(true);
  }, [showSearch]);

  // sections deep in a dynamic zone open the dialog via this event — no prop
  // drilled through the zone dispatcher (see shared-ui.openAppointmentModal)
  useEffect(() => {
    const open = (e: Event) => {
      setModalSource((e as CustomEvent<{ source?: string }>).detail?.source ?? "unknown");
      setShowModal(true);
    };
    const openSearch = () => setShowSearch(true);
    window.addEventListener(OPEN_APPOINTMENT_EVENT, open);
    window.addEventListener(OPEN_SEARCH_EVENT, openSearch);
    return () => {
      window.removeEventListener(OPEN_APPOINTMENT_EVENT, open);
      window.removeEventListener(OPEN_SEARCH_EVENT, openSearch);
    };
  }, []);

  // «/» and Cmd/Ctrl+K open search — unless the visitor is typing somewhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if ((e.key === "/" && !typing) || (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>      <Meta {...props.seo} />
      <BusinessJsonLd footer={props.footer} />
      <AppointmentModal {...{ showModal, setShowModal }} source={modalSource} />
      {searchMounted ? <SearchOverlay open={showSearch} onClose={() => setShowSearch(false)} googleReviews={(props as { googleReviews?: import("@lib/frontend/google-reviews").GoogleReviewsData | null }).googleReviews} footer={props.footer} /> : null}
      <Header
        {...props.header}
        // through openAppointmentModal, NOT a bare setShowModal: the direct
        // call skipped appointment_open entirely — every header-opened
        // dialog was invisible to the funnel (found in P11-d)
        showBookAppointmentModal={() => openAppointmentModal("header")}
        onOpenSearch={() => setShowSearch(true)}
      />
      <main>{props.children}</main>
      <Footer {...props.footer} />
      {/* items 1+6: the phone stays reachable at any scroll depth on mobile,
          where the header CTA is hidden */}
      <PhoneLink variant="floating" placement="floating" showNumber={false} />
      <CookieBanner banner={props.cookieBanner} />
      <BackToTop />
    </>
  );
}
